import type { ApiError, MotnSearchResult } from '@setplex/tria-api'
import type { Effect, EffectParams } from 'effector'

import { api } from '@setplex/tria-api'
import { attach, createEvent, createStore, sample } from 'effector'
import { createAction } from 'effector-action'
import { reset } from 'patronum'
import { isRequestCancelled } from '~/shared/tools/abort'

// last used query and last got result for that query
// used for cache MotN results to avoid spamming wbs with same queries
export const $lastQuery = createStore<string | null>(null)
const $lastResult = createStore<MotnSearchResult | null>(null)

type MovieNightSearchQuery = EffectParams<
  typeof api.externalContent.getMovieNightSearchFx
>

// the caching wrapper (custom `effect` function below) loses effector's Fail
// type inference; annotate it explicitly to the real shape the Nora adapter
// throws, so `.fail`/`.finally` consumers below are typed accurately
export const getMovieNightSearchFx: Effect<
  MovieNightSearchQuery,
  MotnSearchResult,
  ApiError
> = attach({
  source: { lastQuery: $lastQuery, lastResult: $lastResult },
  async effect({ lastQuery, lastResult }, query: MovieNightSearchQuery) {
    return query.q === lastQuery && lastResult != null
      ? lastResult
      : api.externalContent.getMovieNightSearchAbortableFx(query)
  },
}) as unknown as Effect<MovieNightSearchQuery, MotnSearchResult, ApiError>

export const $motnPending = createStore(false)

sample({
  clock: getMovieNightSearchFx,
  fn: () => true,
  target: $motnPending,
})

sample({
  clock: getMovieNightSearchFx.done,
  source: $lastQuery,
  filter: (lastQuery, { params }) => params.q === lastQuery,
  fn: () => false,
  target: $motnPending,
})

sample({
  clock: getMovieNightSearchFx.fail,
  source: $lastQuery,
  filter: (lastQuery, { params, error }) =>
    params.q === lastQuery && !isRequestCancelled(error),
  fn: () => false,
  target: $motnPending,
})

export const resetMotnSearchState = createEvent()

export const $motnSearchResults = createStore<MotnSearchResult | null>(null)

sample({
  clock: getMovieNightSearchFx.done,
  source: $lastQuery,
  filter: (lastQuery, { params }) => params.q === lastQuery,
  fn: (_, { result }) => result,
  target: $motnSearchResults,
})

sample({
  clock: getMovieNightSearchFx.fail,
  source: $lastQuery,
  filter: (lastQuery, { params, error }) =>
    params.q === lastQuery && !isRequestCancelled(error),
  fn: () => null,
  target: $motnSearchResults,
})

// reset last query and last results on new request to wbs
createAction(api.externalContent.getMovieNightSearchFx, {
  target: { $lastQuery, $lastResult },
  fn(target, { q }) {
    target.$lastQuery(q)
    target.$lastResult(null)
  },
})

// save last results in case of successful request result
// `$lastQuery` is intentionally not cleared on failure: every downstream
// `params.q === lastQuery` filter keys on it, and clearing it here would make
// them miss (stuck pending state) whenever an older request fails or is aborted
// after a newer one has already taken over the query slot
createAction(api.externalContent.getMovieNightSearchFx.finally, {
  source: $lastQuery,
  target: { $lastResult },
  fn(target, lastQuery, outcome) {
    if (outcome.status === 'done' && outcome.params.q === lastQuery) {
      target.$lastResult(outcome.result)
    }
  },
})

reset({
  clock: resetMotnSearchState,
  target: [$lastQuery, $lastResult, $motnSearchResults, $motnPending],
})
